import React, { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Urls } from 'data';
import { validateEmail } from 'helpers';
import { useAPI } from 'hooks';
import Locales from 'locales';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription } from 'components/ui/alert';
import { AuthCard, AuthPageLayout } from 'components/shared/AuthPageLayout';
import { Loader2, ArrowLeft, Info, TriangleAlert } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [displayError, setDisplayError] = useState(false);
  const navigate = useNavigate();
  const { api } = useAPI();

  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;

  const { mutate: checkEmail } = useMutation({
    mutationFn: () => api.post('auth/user_token/forgot_password/', { email }),
    onSuccess: () => {
      setDisplayError(false);
      toast.success(Locales.formatString(Locales.auth.forgot_pwd.alert, { email }));
      navigate(Urls.front.login);
    },
  });

  const handleForgotPassword = (event) => {
    event.preventDefault();
    const emptyEmailField = email.trim() === '';
    const validEmail = validateEmail(email);
    if (!emptyEmailField && validEmail) {
      setDisplayError(false);
      checkEmail();
    } else {
      setDisplayError(true);
    }
  };

  const isDisabled = email.trim() === '' || isMutating;

  return (
    <AuthPageLayout className="bg-[#f7f9f8] p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-hajj-primary/5 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-green-200/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
      </div>

      <AuthCard className="max-w-[448px] p-4 max-[520px]:p-4 max-[375px]:p-4">
        <div className="space-y-6">
          <div className="text-left space-y-1 mb-4">
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">{Locales.auth.forgot_pwd.title}</h1>
            <p className="text-sm text-gray-600">Entrez votre email pour réinitialiser votre mot de passe</p>
          </div>

          {displayError && (
            <Alert variant="destructive" className="animate-slide-up flex items-center gap-1">
              <TriangleAlert className="h-4 w-4 text-red-600 mb-2" />
              <AlertDescription>{Locales.auth.email_error_message}</AlertDescription>
            </Alert>
          )}

          <Alert className="rounded-[8px] border-[#9fc5ff] bg-[#eaf3ff] text-[#1f2937]">
            <Info className="h-4 w-4 text-[#26323f]" />
            <AlertDescription>{Locales.auth.forgot_pwd.description}</AlertDescription>
          </Alert>

          <form onSubmit={handleForgotPassword} className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                {Locales.base.field.email}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={Locales.base.field.placeholder.email}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setDisplayError(false);
                }}
                variant="hajj"
                size="sm"
                autoFocus
              />
            </div>

            <Button type="submit" variant="hajj" className="w-full py-[20px]" size="sm" disabled={isDisabled}>
              {isMutating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                Locales.auth.forgot_pwd.action
              )}
            </Button>

            <div className="text-center pt-2">
              <Link
                to={Urls.front.login}
                className="inline-flex items-center text-sm text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {Locales.auth.connect}
              </Link>
            </div>
          </form>
        </div>
      </AuthCard>
    </AuthPageLayout>
  );
};

export default ForgotPassword;
